import { ActionIcon, Alert, Flex, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconListDetails } from '@tabler/icons-react';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { createDocumentsTreeRenderNode } from '@/components/shared/DocumentsTree/createRenderNode';
import Tree from '@/components/shared/Tree';
import FieldCheckbox from '@/components/shared/form/FieldCheckbox';
import Loader from '@/components/ui/Loader';
import { ROUTES } from '@/configs/routes';
import { getDocumentLockMessage } from '@/entities/Document/documentContentLock';
import { getDocumentAvailabilityIconReason } from '@/entities/Tree';
import { useSidebarDocumentsStore } from '@/stores/local/sidebar/SidebarDocumentsStore';
import { navigateToChatWithSession } from '@/utils/chatNavigation';

import FileActions from './FileActions';
import FolderActions from './FolderActions';

import s from './DocumentsTree.module.scss';

type DocumentsTreeProps = {
  withSelection?: boolean;
};

const DocumentsTree: React.FC<DocumentsTreeProps> = ({ withSelection = false }) => {
  const {
    treeStore,
    searchDocumentStore,
    selectionStore,
    pdfNotesIndex,
    popups: { uploadDocumentPopup, deleteDocumentPopup, deleteFolderPopup, createFolderPopup },
  } = useSidebarDocumentsStore();
  const { uploadDocumentStore } = uploadDocumentPopup;
  const navigate = useNavigate();
  const { id } = useParams();

  const handleStopPropagation = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  const handleSelectionNavigate = React.useCallback(() => {
    if (!id) {
      return;
    }

    const selectedDocumentIds = selectionStore.selectedDocumentIds;

    if (!selectedDocumentIds || selectedDocumentIds.length === 0) {
      return;
    }

    if (selectedDocumentIds.length === 1) {
      const [nextDocumentId] = selectedDocumentIds;

      navigateToChatWithSession({ navigate, organizationId: id, documentId: nextDocumentId });

      return;
    }

    navigateToChatWithSession({ navigate, organizationId: id });
  }, [selectionStore, navigate, id]);

  const handleOpenDocumentNotes = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const nextDocumentId = event.currentTarget.dataset.documentId;
      const nextDocumentName = event.currentTarget.dataset.documentName ?? '';

      if (!nextDocumentId) {
        return;
      }

      if (!id) {
        return;
      }

      pdfNotesIndex.requestOpenNotes(nextDocumentId, nextDocumentName);
    },
    [id, pdfNotesIndex],
  );

  const renderNode = React.useMemo(
    () =>
      createDocumentsTreeRenderNode({
        view: 'actions',
        onFileClick: (nodeStore, model) => {
          if (!model.contentAvailable) {
            notifications.show({
              color: 'yellow',
              title: 'Документ недоступен',
              message: getDocumentLockMessage(getDocumentAvailabilityIconReason(model)),
              autoClose: 6000,
            });

            return;
          }

          if (withSelection) {
            selectionStore.toggleNode(nodeStore);
            handleSelectionNavigate();

            return;
          }

          navigate(ROUTES.organization.documents.document.create(String(id), model.id), {
            state: { name: model.name },
          });
        },
        renderFileActions: (nodeStore, model) => (
          <FileActions
            nodeStore={nodeStore}
            model={model}
            deletePopupStore={deleteDocumentPopup}
            withSelection={withSelection}
            onAfterToggle={handleSelectionNavigate}
          />
        ),
        renderFileIndicator: (_nodeStore, model) => {
          if (!model.contentAvailable) {
            return null;
          }

          const hasNotes = pdfNotesIndex.meta.isLoaded && pdfNotesIndex.hasNotes(model.id);

          if (!hasNotes || !id) {
            return null;
          }

          return (
            <Tooltip label="Открыть список заметок" withArrow>
              <ActionIcon
                variant="subtle"
                size="sm"
                className={s['root__notes-indicator']}
                data-document-id={model.id}
                data-document-name={model.name}
                onClick={handleOpenDocumentNotes}
                aria-label="Открыть список заметок по документу"
              >
                <IconListDetails size={14} className={s['root__notes-indicator__icon']} />
              </ActionIcon>
            </Tooltip>
          );
        },
        renderFolderActions: (store, model) => (
          <FolderActions
            model={model}
            treeStore={store}
            uploadDocumentPopup={uploadDocumentPopup}
            editFolderPopupStore={createFolderPopup}
            deletePopupStore={deleteFolderPopup}
            withSelection={withSelection}
          />
        ),
      }),
    [
      deleteDocumentPopup,
      deleteFolderPopup,
      createFolderPopup,
      uploadDocumentPopup,
      navigate,
      id,
      withSelection,
      selectionStore,
      handleSelectionNavigate,
      handleOpenDocumentNotes,
      pdfNotesIndex,
    ],
  );

  if (treeStore.meta.isLoading || searchDocumentStore.meta.isLoading) {
    return <Loader gap={0} fullHeight type="dots" size="xl" text="Ищу документы..." />;
  }

  if (uploadDocumentStore.meta.isLoading) {
    const { uploadTotal, uploadCompleted } = uploadDocumentStore;
    const loaderText = `Загружаю документ${uploadTotal > 1 ? 'ы' : ''}... (${uploadCompleted} из ${uploadTotal})`;

    return <Loader gap={0} fullHeight type="dots" size="xl" text={loaderText} />;
  }

  if (treeStore.meta.isError) {
    return (
      <Alert ml="lg" mr="lg" color="red">
        Не удалось загрузить документы
      </Alert>
    );
  }

  if (treeStore.children.length === 0) {
    return (
      <Alert ml="lg" mr="lg">
        Документы не загружены
      </Alert>
    );
  }

  if (!withSelection) {
    return <Tree store={treeStore} renderNode={renderNode} className={s['tree-only']} />;
  }

  const rootNodeState = selectionStore.getNodeState(treeStore);

  return (
    <Flex className={s.root} direction="column" align="flex-start" gap="sm">
      <Flex className={s.root__header} align="center" justify="space-between" gap="sm">
        <Text size="sm">Выбрать все источники</Text>
        <FieldCheckbox
          checked={rootNodeState.checked}
          indeterminate={rootNodeState.indeterminate}
          onToggle={selectionStore.toggleSelectAll}
          onClick={handleStopPropagation}
        />
      </Flex>
      <Tree store={treeStore} renderNode={renderNode} className={s.root__tree} />
    </Flex>
  );
};

export default observer(DocumentsTree);
